import { useCallback, useEffect, useState } from 'react';

/**
 * Per-device memory of which optional table columns a clinician has hidden.
 *
 * Stored as the HIDDEN set rather than the visible one so that a column added
 * to the table later shows up for everyone by default instead of silently
 * staying off for anyone who had already saved a preference.
 */
const read = (storageKey: string, fallback: string[]): Set<string> => {
  try {
    const raw = window.localStorage.getItem(storageKey);
    if (raw === null) return new Set(fallback);
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return new Set(fallback);
    return new Set(parsed.filter((k): k is string => typeof k === 'string'));
  } catch {
    // Private mode, blocked storage, or malformed JSON — fall back to the
    // defaults rather than failing the render.
    return new Set(fallback);
  }
};

export const useTableColumns = (storageKey: string, defaultHidden: string[]) => {
  const [hidden, setHidden] = useState<Set<string>>(() => read(storageKey, defaultHidden));

  useEffect(() => {
    try {
      window.localStorage.setItem(storageKey, JSON.stringify([...hidden]));
    } catch {
      // Preference is a convenience; losing it must not break the page.
    }
  }, [storageKey, hidden]);

  const toggle = useCallback((key: string) => {
    setHidden((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const reset = useCallback(() => setHidden(new Set(defaultHidden)), [defaultHidden]);

  const isVisible = useCallback((key: string) => !hidden.has(key), [hidden]);

  return { hidden, isVisible, toggle, reset };
};
